import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import "./salesOverview.css";
import { useHeaderSlot } from "../components/GlobalHeader";
import {
  getSalesManagers,
  setSalesTarget,
  getSalesOverview,
  getSalesOverviewAll,
  downloadSalesOverviewExport,
  downloadSalesOverviewExportAll,
  getSalesOverviewOperatorDetail,
  getSalesOverviewAllOperatorDetail,
  getCurrentUser,
} from "../api/api";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";
import {
  exportSalesOverviewPdf,
  exportSalesOverviewAllManagersPdf,
} from "../components/utils/exportSalesOverviewPdf";

import manager1 from "../assets/managers/profile.jpg";
import manager2 from "../assets/managers/profile.jpg";
import manager3 from "../assets/managers/profile.jpg";
import manager4 from "../assets/managers/profile.jpg";
import manager5 from "../assets/managers/profile.jpg";
import manager6 from "../assets/managers/profile.jpg";
import manager7 from "../assets/managers/profile.jpg";
import manager8 from "../assets/managers/profile.jpg";

const SALES_MANAGER_LOGIN_MAP = {
  rajini: 1,
  rajesh: 2,
  balaji: 3,
  magesh: 4,
  rajasekar: 5,
  saravanan: 6,
  mageshkumar: 7,
  esakiraj: 8,
};

const MANAGER_PHOTOS = {
  1: manager1,
  2: manager2,
  3: manager3,
  4: manager4,
  5: manager5,
  6: manager6,
  7: manager7,
  8: manager8,
};

const ALL_MANAGER_IDS = [1, 2, 3, 4, 5, 6, 7, 8];

function useAccessControl() {
  const username = (getCurrentUser() || "").trim().toLowerCase();
  const restrictedManagerId = SALES_MANAGER_LOGIN_MAP[username];
  const isRestrictedSalesManager = restrictedManagerId !== undefined;
  return {
    isRestrictedSalesManager,
    restrictedManagerId: isRestrictedSalesManager ? restrictedManagerId : null,
    canSetTarget: !isRestrictedSalesManager,
  };
}

const toMonthKey = (ym) => (ym || "").slice(0, 7);

const normalizeRow = (r) => ({
  id: r.id,
  month: toMonthKey(r.month),
  target: r.target === null || r.target === undefined ? null : Number(r.target),
  stb_tobe_issued:
    r.stb_tobe_issued === null || r.stb_tobe_issued === undefined
      ? null
      : Number(r.stb_tobe_issued),
  stb_issued: Number(r.stb_issued) || 0,
  achieved: Number(r.achieved) || 0,
});

function fillAllManagerRows(rows, managers, monthKey) {
  const byId = new Map(rows.map((r) => [Number(r.id), r]));
  const managerById = new Map((managers || []).map((m) => [Number(m.id), m]));
  return ALL_MANAGER_IDS.map((id) => {
    const existing = byId.get(id);
    if (existing) return existing;
    const mgr = managerById.get(id);
    return {
      id,
      name: mgr?.name || `Manager ${id}`,
      month: monthKey || "",
      target: null,
      stb_tobe_issued: null,
      stb_issued: 0,
      achieved: 0,
    };
  });
}

const monthLabel = (ym) => {
  const [y, m] = toMonthKey(ym).split("-");
  return new Date(Number(y), Number(m) - 1).toLocaleString("en-US", {
    month: "short",
    year: "2-digit",
  });
};
const monthLabelLong = (ym) => {
  const [y, m] = toMonthKey(ym).split("-");
  return new Date(Number(y), Number(m) - 1).toLocaleString("en-US", {
    month: "long",
    year: "numeric",
  });
};
const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const initials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

const AVATAR_GRADIENTS = [
  ["#6d93ff", "#2a5fd6"],
  ["#6ff5d4", "#0f9d63"],
  ["#fbbf24", "#d97706"],
  ["#f87171", "#dc2626"],
  ["#c084fc", "#7e22ce"],
  ["#38bdf8", "#0369a1"],
  ["#fb923c", "#c2410c"],
  ["#34d399", "#047857"],
];
function avatarGradient(id) {
  const idx = Math.abs(Number(id) || 0) % AVATAR_GRADIENTS.length;
  const [a, b] = AVATAR_GRADIENTS[idx];
  return `linear-gradient(135deg, ${a}, ${b})`;
}

function achTierColor(pct) {
  if (pct >= 100) return "#0f9d63";
  if (pct >= 70) return "#b45309";
  if (pct > 0) return "#dc2626";
  return "#94a3b8";
}

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() =>
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia(query).matches
      : false,
  );
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia(query);
    const handler = (e) => setMatches(e.matches);
    setMatches(mql.matches);
    if (mql.addEventListener) mql.addEventListener("change", handler);
    else mql.addListener(handler);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", handler);
      else mql.removeListener(handler);
    };
  }, [query]);
  return matches;
}

function useAnimatedNumber(target, duration = 800) {
  const [display, setDisplay] = useState(0);
  const rafRef = useRef(null);
  const startRef = useRef(null);
  const fromRef = useRef(0);
  useEffect(() => {
    fromRef.current = display;
    startRef.current = null;
    const step = (ts) => {
      if (!startRef.current) startRef.current = ts;
      const progress = Math.min((ts - startRef.current) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(
        Math.round(fromRef.current + (target - fromRef.current) * eased),
      );
      if (progress < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);
  return display;
}

function SaveTargetToast({ show, onClose, managerName, month, target }) {
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    if (!show) return;
    setClosing(false);
    const closeTimer = setTimeout(() => setClosing(true), 2600);
    const removeTimer = setTimeout(() => onClose(), 2950);
    return () => {
      clearTimeout(closeTimer);
      clearTimeout(removeTimer);
    };
  }, [show, onClose]);

  if (!show) return null;

  return (
    <div className={`so-toast-wrap${closing ? " so-toast-out" : ""}`}>
      <div className="so-toast">
        <div className="so-toast-shine" />
        <div className="so-toast-check">
          <svg viewBox="0 0 24 24" width="20" height="20">
            <circle cx="12" cy="12" r="11" fill="none" />
            <path
              className="so-toast-check-path"
              d="M6.5 12.5l4 4 7-8.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <div className="so-toast-body">
          <div className="so-toast-title">Target saved</div>
          <div className="so-toast-sub">
            {managerName ? `${managerName} · ` : ""}
            {monthLabel(month)} ·{" "}
            <strong>{Number(target).toLocaleString("en-IN")}</strong> STB
          </div>
        </div>
        <button
          type="button"
          className="so-toast-close"
          onClick={() => setClosing(true)}
        >
          ✕
        </button>
        <div className="so-toast-progress" />
      </div>
    </div>
  );
}

let keyframesInjected = false;
function injectKeyframesOnce() {
  if (keyframesInjected || typeof document === "undefined") return;
  keyframesInjected = true;
  const style = document.createElement("style");
  style.textContent = `
    @keyframes so-rec-pulse {
      0% { box-shadow: 0 0 0 0 rgba(255,91,106,0.55); }
      70% { box-shadow: 0 0 0 7px rgba(255,91,106,0); }
      100% { box-shadow: 0 0 0 0 rgba(255,91,106,0); }
    }
    @keyframes so-row-in {
      from { opacity: 0; transform: translateY(8px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes so-fill-shimmer {
      0% { transform: translateX(-100%); }
      100% { transform: translateX(220%); }
    }
    @keyframes so-badge-pop {
      0% { transform: scale(0.6); opacity: 0; }
      60% { transform: scale(1.12); opacity: 1; }
      100% { transform: scale(1); opacity: 1; }
    }
    @keyframes so-card-in {
      from { opacity: 0; transform: translateY(6px) scale(0.98); }
      to { opacity: 1; transform: translateY(0) scale(1); }
    }
    @keyframes so-spin {
      to { transform: rotate(360deg); }
    }
    @keyframes so-loader-fade-in {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    .so-skeleton-block {
      position: relative;
      overflow: hidden;
      background: #e7edf7;
      display: inline-block;
    }
    .so-skeleton-block::after {
      content: "";
      position: absolute;
      inset: 0;
      background: linear-gradient(90deg, transparent, rgba(255,255,255,0.75), transparent);
      animation: so-fill-shimmer 1.4s infinite;
    }
    .so-mgr-card:hover {
      transform: translateY(-2px);
      box-shadow: 0 10px 22px rgba(15,58,125,0.14);
    }
    .so-topbar-select {
      appearance: none;
      -webkit-appearance: none;
      background-image: linear-gradient(45deg, transparent 50%, #5c6f8f 50%), linear-gradient(135deg, #5c6f8f 50%, transparent 50%);
      background-position: calc(100% - 16px) center, calc(100% - 11px) center;
      background-size: 5px 5px, 5px 5px;
      background-repeat: no-repeat;
    }
    /* ---- section overlay loader (keeps layout height stable, no scroll jump) ---- */
    .so-section-loading-wrap {
      position: relative;
    }
    .so-section-overlay {
      position: absolute;
      inset: 0;
      z-index: 5;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 12px;
      background: rgba(255,255,255,0.88);
      backdrop-filter: blur(1.5px);
      border-radius: 14px;
      animation: so-loader-fade-in .15s ease both;
    }
    .so-section-spinner {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      border: 3px solid #d7e0f0;
      border-top-color: #0f3a7d;
      animation: so-spin 0.8s linear infinite;
    }
    .so-section-overlay-text {
      font-size: 13.5px;
      font-weight: 700;
      color: #5c6f8f;
    }
    .so-section-dimmed {
      opacity: 0.35;
      pointer-events: none;
      filter: saturate(0.7);
      transition: opacity .15s ease;
    }
    /* ---- global page scroll layout (no inner scroll panel) ---- */
    .so-fullscreen {
      display: flex;
      flex-direction: column;
      box-sizing: border-box;
      gap: 8px;
    }
    .so-scroll-panel {
      /* scrolling now happens on the page itself, not this panel */
    }
    /* ---- manager ID-card grid — 6 per row on desktop ---- */
    .so-mgr-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 25px;
      margin-left: auto;
      margin-right: auto;
      /* keeps the intended 1220px size on screens wide enough for it, but
         stops it from ever being wider than the space actually available
         (e.g. a 1366px-wide laptop with the sidebar taking ~220px) — that
         mismatch, not the centering itself, is what pushes cards off the
         right edge of the screen. */
      max-width: 100%;
      box-sizing: border-box;
    }
    .so-mgr-id-card {
      transition: transform .15s ease, box-shadow .15s ease;
      min-width: 0;
    }
    .so-mgr-id-card:hover {
      transform: translateY(-3px);
      box-shadow: 0 14px 26px rgba(15,58,125,0.16);
    }
    /* ---- tab bar ---- */
    .so-tab-btn {
      border: 1px solid #d7e0f0;
      background: #fff;
      color: #5c6f8f;
      font-weight: 700;
      font-size: 13.5px;
      padding: 8px 18px;
      border-radius: 9px;
      cursor: pointer;
      transition: background .15s ease, color .15s ease, border-color .15s ease;
    }
    .so-tab-btn.so-tab-active {
      background: #0f3a7d;
      border-color: #0f3a7d;
      color: #fff;
    }

    /* =========================================================
       RESPONSIVE — tablet (768px–1024px) and mobile (<768px)
       ========================================================= */

    /* ---- Tablet: 768px – 1024px — 3 manager cards per row ---- */
    @media (max-width: 1024px) {
      .so-mgr-grid {
        grid-template-columns: repeat(3, 1fr) !important;
        gap: 16px !important;
        margin-left: 0px !important;
        /* the desktop view pins this grid to a fixed 1220px/610px box —
           on tablet that fixed box no longer matches the number of rows
           (3-per-row instead of 4-per-row) and doesn't fit the viewport,
           which is what breaks the card layout. Let it size naturally. */
        width: 100% !important;
        height: auto !important;
      }
      .so-mgr-id-card { height: auto !important; }
      .so-container { padding: 14px 16px !important; }
      .so-card { padding: 18px 16px !important; }
      .so-summary-row-responsive { gap: 10px !important; }
      .so-topbar-responsive { padding: 12px 14px !important; }
      .recharts-responsive-container { min-width: 0 !important; }
    }

    /* ---- Mobile: below 768px — manager cards as a compact 2-up photo grid ---- */
    @media (max-width: 767px) {
      .so-mgr-grid {
        grid-template-columns: repeat(2, 1fr) !important;
        gap: 10px !important;
        margin-left: 0px !important;
        /* same fixed-size issue as tablet, just more severe at this width */
        width: 100% !important;
        height: auto !important;
      }
      .so-mgr-id-card { height: auto !important; }
      .so-mgr-photo { aspect-ratio: 1 / 1 !important; }
      .so-container { padding: 8px 8px !important; }
      .so-card { padding: 14px 12px !important; border-radius: 14px !important; }
      .so-summary-row-responsive { flex-direction: column !important; gap: 10px !important; }
      .so-summary-row-responsive > div { min-width: 0 !important; width: 100%; }
      .so-topbar-responsive { flex-wrap: wrap !important; row-gap: 10px; padding: 12px !important; }
      .so-topbar-responsive > div { flex-wrap: wrap !important; }
      .so-nav-controls-responsive { width: 100%; }
      .so-nav-controls-responsive > * { flex: 1 1 auto; }
      .so-card-title { flex-direction: column !important; align-items: flex-start !important; }
      .so-card-title .so-topbar-select,
      .so-combobox { width: 100% !important; max-width: none !important; min-width: 0 !important; }
      .recharts-responsive-container { min-width: 0 !important; width: 100% !important; }
      .so-race-table { min-width: 620px !important; }
      .so-tar-table { min-width: 480px !important; }
      .so-modal-apply-all { flex-direction: column !important; align-items: stretch !important; }
      .so-field-btn { width: 100% !important; }
      .so-apply-all-btn { width: 100%; }
    }

    /* ---- Extra-narrow phones: below 420px — drop to 1 card per row ---- */
    @media (max-width: 420px) {
      .so-mgr-grid { grid-template-columns: 1fr !important; }
      .so-mgr-photo { aspect-ratio: 16 / 10 !important; }
    }
  `;
  document.head.appendChild(style);
}

function ChartDefs() {
  return (
    <defs>
      <linearGradient id="gradTargetLine" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#6d93ff" />
        <stop offset="100%" stopColor="#2a5fd6" />
      </linearGradient>
      <linearGradient id="gradAchievedLine" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#6ff5d4" />
        <stop offset="100%" stopColor="#1fd393" />
      </linearGradient>
      <filter id="raceGlow" x="-80%" y="-80%" width="260%" height="260%">
        <feGaussianBlur stdDeviation="4.5" result="blur" />
        <feMerge>
          <feMergeNode in="blur" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>
  );
}

function formatY(v) {
  if (v === 0) return "0";
  if (v >= 1000) return `${parseFloat((v / 1000).toFixed(1))}k`;
  return String(v);
}

function CustomXTick({ x, y, payload, monthByIdx, compact }) {
  const label = monthByIdx?.[Math.round(payload.value)] ?? "";
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        textAnchor="middle"
        dy={16}
        style={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize: compact ? "11px" : "13.5px",
          fontWeight: 700,
          fill: "#dce8ff",
        }}
      >
        {label}
      </text>
    </g>
  );
}
function CustomYTick({ x, y, payload, compact }) {
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        textAnchor="end"
        dy={4}
        style={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize: compact ? "11px" : "13px",
          fontWeight: 700,
          fill: "#b9caea",
        }}
      >
        {formatY(payload.value)}
      </text>
    </g>
  );
}

function CustomTooltip({ active, payload, monthByIdx }) {
  if (!active || !payload?.length) return null;
  const idx = Math.round(payload[0]?.payload?.idx ?? 0);
  const label = monthByIdx?.[idx] ?? "";
  const target = payload.find((p) => p.dataKey === "Target")?.value ?? 0;
  const achieved = payload.find((p) => p.dataKey === "Achieved")?.value ?? 0;
  const pct = target ? Math.round((achieved / target) * 100) : 0;
  return (
    <div className="so-chart-tooltip">
      <div className="so-tt-shimmer" />
      <div className="so-tt-title">{label}</div>
      <div className="so-tt-divider" />
      <div className="so-tt-row">
        <span
          className="so-tt-dot"
          style={{ background: "linear-gradient(135deg,#6d93ff,#2a5fd6)" }}
        />
        <span className="so-tt-label">Target</span>
        <strong className="so-tt-value">
          {Math.round(target).toLocaleString("en-IN")}
        </strong>
      </div>
      <div className="so-tt-row">
        <span
          className="so-tt-dot"
          style={{ background: "linear-gradient(135deg,#6ff5d4,#1fd393)" }}
        />
        <span className="so-tt-label">Achieved</span>
        <strong className="so-tt-value">
          {Math.round(achieved).toLocaleString("en-IN")}
        </strong>
      </div>
      <div className="so-tt-divider" />
      <div className="so-tt-row">
        <span className="so-tt-label">Achievement</span>
        <strong
          className="so-tt-value"
          style={{
            color: pct >= 100 ? "#6ff5d4" : pct >= 70 ? "#fbbf6a" : "#ff8080",
          }}
        >
          {pct}%
        </strong>
      </div>
    </div>
  );
}

function BarTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const target = payload.find((p) => p.dataKey === "target")?.value ?? 0;
  const achieved = payload.find((p) => p.dataKey === "achieved")?.value ?? 0;
  const pct = target ? Math.round((achieved / target) * 100) : 0;
  return (
    <div className="so-chart-tooltip">
      <div className="so-tt-title">{label}</div>
      <div className="so-tt-divider" />
      <div className="so-tt-row">
        <span
          className="so-tt-dot"
          style={{ background: "linear-gradient(135deg,#6d93ff,#2a5fd6)" }}
        />
        <span className="so-tt-label">Target</span>
        <strong className="so-tt-value">
          {Math.round(target).toLocaleString("en-IN")}
        </strong>
      </div>
      <div className="so-tt-row">
        <span
          className="so-tt-dot"
          style={{ background: "linear-gradient(135deg,#6ff5d4,#1fd393)" }}
        />
        <span className="so-tt-label">Achieved</span>
        <strong className="so-tt-value">
          {Math.round(achieved).toLocaleString("en-IN")}
        </strong>
      </div>
      <div className="so-tt-divider" />
      <div className="so-tt-row">
        <span className="so-tt-label">Achievement</span>
        <strong className="so-tt-value" style={{ color: achTierColor(pct) }}>
          {pct}%
        </strong>
      </div>
    </div>
  );
}

function RaceDot(stroke, plotWidth, direction = "up") {
  return function DotRenderer(props) {
    const { cx, cy, index, dataLength, value } = props;
    if (cx == null || cy == null) return null;
    const isLeader = props?.payload?.isLeader ?? index === dataLength - 1;
    if (!isLeader) {
      return <circle cx={cx} cy={cy} r={2.5} fill={stroke} opacity={0.55} />;
    }

    const label = Math.round(value).toLocaleString("en-IN");
    const bubbleW = Math.max(46, label.length * 8 + 18);
    const bubbleH = 20;

    let bx = cx - bubbleW / 2;
    const minX = 4;
    const maxX = (plotWidth || 9999) - bubbleW - 4;
    if (bx > maxX) bx = maxX;
    if (bx < minX) bx = minX;

    const isUp = direction === "up";
    const gap = 22;
    const by = isUp ? cy - gap - bubbleH : cy + gap;
    const pointerX = Math.min(Math.max(cx - bx, 10), bubbleW - 10);

    const pointerPath = isUp
      ? `M${pointerX - 5},${bubbleH - 1} L${pointerX + 5},${bubbleH - 1} L${pointerX},${bubbleH + 6} Z`
      : `M${pointerX - 5},1 L${pointerX + 5},1 L${pointerX},-6 Z`;

    return (
      <g filter="url(#raceGlow)">
        <circle cx={cx} cy={cy} r={12} fill={stroke} opacity={0.18}>
          <animate
            attributeName="r"
            values="10;16;10"
            dur="1.6s"
            repeatCount="indefinite"
          />
        </circle>
        <circle
          cx={cx}
          cy={cy}
          r={5.5}
          fill="#0c1830"
          stroke={stroke}
          strokeWidth={3}
        />
        <circle cx={cx} cy={cy} r={2.2} fill="#fff" />
        <g transform={`translate(${bx},${by})`}>
          <rect
            x={0}
            y={0}
            rx={7}
            ry={7}
            width={bubbleW}
            height={bubbleH}
            fill={stroke}
          />
          <path d={pointerPath} fill={stroke} />
          <text
            x={bubbleW / 2}
            y={bubbleH / 2 + 4}
            textAnchor="middle"
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: "11px",
              fontWeight: 800,
              fill: "#04101f",
            }}
          >
            {label}
          </text>
        </g>
      </g>
    );
  };
}

const SPEED_OPTIONS = [
  { label: "0.5×", value: 0.5 },
  { label: "1×", value: 1 },
  { label: "1.75×", value: 1.75 },
];

function useLineChartRace(fullData, { msPerMonth = 2600, autoPlayKey } = {}) {
  const n = fullData.length;
  const [progress, setProgress] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);

  const rafRef = useRef(null);
  const lastTsRef = useRef(null);
  const speedRef = useRef(1);
  const progressRef = useRef(0);

  useEffect(() => {
    speedRef.current = speed;
  }, [speed]);

  const stopLoop = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    lastTsRef.current = null;
  };

  const tick = useCallback(
    (ts) => {
      if (lastTsRef.current == null) lastTsRef.current = ts;
      const dt = ts - lastTsRef.current;
      lastTsRef.current = ts;
      const next = Math.min(
        progressRef.current + (dt / msPerMonth) * speedRef.current,
        n - 1,
      );
      progressRef.current = next;
      setProgress(next);
      if (next >= n - 1) {
        stopLoop();
        setIsPlaying(false);
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    },
    [msPerMonth, n],
  );

  const play = useCallback(() => {
    if (n <= 1) return;
    if (progressRef.current >= n - 1) {
      progressRef.current = 0;
      setProgress(0);
    }
    stopLoop();
    setIsPlaying(true);
    rafRef.current = requestAnimationFrame(tick);
  }, [n, tick]);

  const pause = useCallback(() => {
    stopLoop();
    setIsPlaying(false);
  }, []);

  const replay = useCallback(() => {
    stopLoop();
    progressRef.current = 0;
    setProgress(0);
    setIsPlaying(true);
    rafRef.current = requestAnimationFrame(tick);
  }, [tick]);

  useEffect(() => {
    stopLoop();
    progressRef.current = 0;
    setProgress(0);
    if (n > 0) {
      setIsPlaying(true);
      rafRef.current = requestAnimationFrame(tick);
    } else {
      setIsPlaying(false);
    }
    return stopLoop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPlayKey, n]);

  useEffect(() => stopLoop, []);

  return { progress, isPlaying, speed, setSpeed, play, pause, replay };
}

function MonthYearPicker({ value, onChange, minYear = 2023 }) {
  // value is "YYYY-MM"
  const [year, month] = value.split("-").map(Number);
  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(year);
  const wrapRef = useRef(null);

  const currentYear = new Date().getFullYear();
  const currentMonthNum = new Date().getMonth() + 1;

  useEffect(() => {
    if (open) setViewYear(year);
  }, [open, year]);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const MONTHS = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const isMonthDisabled = (m) =>
    (viewYear === currentYear && m > currentMonthNum) || viewYear > currentYear;

  const canGoPrevYear = viewYear - 1 >= minYear;
  const canGoNextYear = viewYear + 1 <= currentYear;

  const handleMonthClick = (m) => {
    if (isMonthDisabled(m)) return;
    onChange(`${viewYear}-${String(m).padStart(2, "0")}`);
    setOpen(false);
  };

  const displayLabel = new Date(year, month - 1).toLocaleString("en-US", {
    month: "short",
    year: "numeric",
  });

  return (
    <div
      ref={wrapRef}
      style={{ position: "relative", display: "inline-block" }}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          height: 40,
          padding: "0 16px",
          borderRadius: 10,
          border: "1px solid #d7e0f0",
          background: "#fff",
          color: "#1e3a5f",
          fontWeight: 700,
          fontSize: 14,
          minWidth: 150,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
        }}
      >
        <span>📅 {displayLabel}</span>
        <span
          style={{
            fontSize: 10,
            color: "#5c6f8f",
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform .15s ease",
          }}
        >
          ▼
        </span>
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            left: 0,
            zIndex: 50,
            background: "#fff",
            borderRadius: 16,
            boxShadow: "0 12px 32px rgba(15,58,125,0.22)",
            border: "1px solid #e3eaf6",
            padding: "16px 18px 18px",
            width: 300,
          }}
        >
          {/* Year navigator */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 16,
            }}
          >
            <button
              type="button"
              onClick={() => canGoPrevYear && setViewYear((y) => y - 1)}
              disabled={!canGoPrevYear}
              style={{
                border: "none",
                background: "transparent",
                fontSize: 18,
                color: canGoPrevYear ? "#0f3a7d" : "#c7d2e6",
                cursor: canGoPrevYear ? "pointer" : "default",
                padding: "4px 10px",
                borderRadius: 8,
                fontWeight: 700,
              }}
            >
              ‹
            </button>
            <span
              style={{
                fontSize: 19,
                fontWeight: 800,
                color: "#1e3a5f",
                letterSpacing: 0.3,
              }}
            >
              {viewYear}
            </span>
            <button
              type="button"
              onClick={() => canGoNextYear && setViewYear((y) => y + 1)}
              disabled={!canGoNextYear}
              style={{
                border: "none",
                background: "transparent",
                fontSize: 18,
                color: canGoNextYear ? "#0f3a7d" : "#c7d2e6",
                cursor: canGoNextYear ? "pointer" : "default",
                padding: "4px 10px",
                borderRadius: 8,
                fontWeight: 700,
              }}
            >
              ›
            </button>
          </div>

          {/* Month grid */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              rowGap: 18,
              columnGap: 4,
            }}
          >
            {MONTHS.map((label, idx) => {
              const m = idx + 1;
              const disabled = isMonthDisabled(m);
              const isSelected = viewYear === year && m === month;
              return (
                <button
                  key={m}
                  type="button"
                  onClick={() => handleMonthClick(m)}
                  disabled={disabled}
                  style={{
                    border: "none",
                    background: isSelected
                      ? "linear-gradient(135deg,#2a5fd6,#0f3a7d)"
                      : "transparent",
                    color: disabled
                      ? "#c7d2e6"
                      : isSelected
                        ? "#fff"
                        : "#1e3a5f",
                    fontWeight: isSelected ? 800 : 600,
                    fontSize: 14,
                    width: 56,
                    height: 40,
                    borderRadius: 999,
                    cursor: disabled ? "default" : "pointer",
                    justifySelf: "center",
                    transition: "background .15s ease, color .15s ease",
                    boxShadow: isSelected
                      ? "0 4px 10px rgba(15,58,125,0.35)"
                      : "none",
                  }}
                  onMouseEnter={(e) => {
                    if (!disabled && !isSelected)
                      e.currentTarget.style.background = "#eef3fb";
                  }}
                  onMouseLeave={(e) => {
                    if (!disabled && !isSelected)
                      e.currentTarget.style.background = "transparent";
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function SalesOverviewNavControls({
  monthFilter,
  setMonthFilter,
  onSearch,
  monthLoading,
  canSetTarget,
  onSetTarget,
}) {
  return (
    <div
      className="so-nav-controls-responsive"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        flexWrap: "wrap",
      }}
    >
      <MonthYearPicker
        value={monthFilter}
        onChange={setMonthFilter}
        minYear={2023}
      />
      <button
        type="button"
        onClick={onSearch}
        disabled={monthLoading}
        style={{
          height: 40,
          padding: "0 18px",
          borderRadius: 10,
          border: "none",
          background: "rgba(255,255,255,0.16)",
          color: "#fff",
          fontWeight: 700,
          fontSize: 14,
          cursor: "pointer",
        }}
      >
        {monthLoading ? "Searching..." : "🔍 Search"}
      </button>
      {canSetTarget && (
        <button
          type="button"
          onClick={onSetTarget}
          style={{
            height: 40,
            padding: "0 18px",
            borderRadius: 10,
            border: "none",
            background: "#10b981",
            color: "#fff",
            fontWeight: 700,
            fontSize: 14,
            cursor: "pointer",
            boxShadow: "0 4px 10px rgba(16,185,129,0.35)",
          }}
        >
          🎯 Set Target
        </button>
      )}
    </div>
  );
}

function CompactTopBar({
  managerCount,
  monthFilter,
  setMonthFilter,
  onSearch,
  monthLoading,
  canSetTarget,
  onSetTarget,
}) {
  return (
    <div
      className="so-topbar-responsive"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        flexWrap: "wrap",
        gap: 16,
        padding: "10px 16px",
        borderRadius: 12,
        background: "linear-gradient(90deg,#0f3a7d,#00a3d9)",
        boxShadow: "0 6px 18px rgba(15,58,125,0.22)",
        flexShrink: 0,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{ fontSize: 20, lineHeight: 1 }}>🎯</span>
        <div>
          <div style={{ color: "#fff", fontWeight: 800, fontSize: 16 }}>
            Sales Overview
          </div>
          <div
            style={{
              color: "rgba(255,255,255,0.78)",
              fontSize: 11.5,
              fontWeight: 600,
              whiteSpace: "nowrap",
            }}
          >
            {managerCount} Managers · Targets & Achievement
          </div>
        </div>
      </div>

      <SalesOverviewNavControls
        monthFilter={monthFilter}
        setMonthFilter={setMonthFilter}
        onSearch={onSearch}
        monthLoading={monthLoading}
        canSetTarget={canSetTarget}
        onSetTarget={onSetTarget}
      />
    </div>
  );
}

function MiniSummaryCard({ label, emoji, value, accent, foot }) {
  const isNumeric = typeof value === "number";
  const animated = useAnimatedNumber(isNumeric ? value : 0);
  return (
    <div
      style={{
        flex: 1,
        minWidth: 160,
        position: "relative",
        overflow: "hidden",
        background: "#fff",
        borderRadius: 14,
        padding: "18px 22px",
        boxShadow: "0 2px 10px rgba(15,58,125,0.08)",
        border: "1px solid rgba(15,58,125,0.06)",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: accent,
        }}
      />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          fontSize: 15.5,
          fontWeight: 700,
          letterSpacing: 0.3,
          color: "#6b7c96",
          textTransform: "uppercase",
        }}
      >
        <span>{emoji}</span>
        {label}
      </div>
      <div
        style={{
          fontSize: 30,
          fontWeight: 800,
          color: accent,
          lineHeight: 1.5,
        }}
      >
        {isNumeric ? animated.toLocaleString("en-IN") : value}
      </div>
      {foot && (
        <div style={{ fontSize: 14.5, color: "#94a3b8", fontWeight: 600 }}>
          {foot}
        </div>
      )}
    </div>
  );
}

function ManagerIdCard({ row }) {
  const [imgFailed, setImgFailed] = useState(false);
  const pct = row.target ? Math.round((row.achieved / row.target) * 100) : 0;
  const grad = avatarGradient(row.id);
  const barColor =
    pct >= 100
      ? "linear-gradient(90deg,#34d399,#059669)"
      : pct >= 70
        ? "linear-gradient(90deg,#fbbf24,#f59e0b)"
        : "linear-gradient(90deg,#f87171,#dc2626)";

  return (
    <div
      className="so-mgr-id-card"
      style={{
        background: "#fff",
        borderRadius: 14,
        overflow: "hidden",
        border: "1px solid rgba(15,58,125,0.08)",
        boxShadow: "0 6px 16px rgba(15,58,125,0.09)",
        display: "flex",
        flexDirection: "column",
        // marginBottom: "45px",
      }}
    >
      <div
        className="so-mgr-photo"
        style={{
          position: "relative",
          width: "100%",
          aspectRatio: "9/7",
          background: grad,
          overflow: "hidden",
        }}
      >
        <span
          className="so-ach-badge"
          style={{
            position: "absolute",
            top: 6,
            right: 6,
            fontSize: 18,
            padding: "2px 7px",
            background: achTierColor(pct),
            color: "#fff",
            zIndex: 2,
          }}
        >
          {pct}%
        </span>

        {!imgFailed ? (
          <img
            src={MANAGER_PHOTOS[row.id] || manager1}
            alt={row.name}
            onError={() => setImgFailed(true)}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        ) : (
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              fontWeight: 800,
              fontSize: 22,
            }}
          >
            {initials(row.name)}
          </div>
        )}
      </div>
      <div style={{ textAlign: "center", padding: "7px 8px 8px" }}>
        <div
          style={{
            fontSize: 20,
            fontWeight: 800,
            color: "#1e3a5f",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={row.name}
        >
          {row.name}
        </div>
        {/* <div
          style={{
            fontSize: 9.5,
            fontWeight: 700,
            color: "#94a3b8",
            textTransform: "uppercase",
            letterSpacing: 0.3,
            marginBottom: 5,
          }}
        >
          Sales Manager
        </div> */}

        <div
          style={{
            height: 1,
            background: "rgba(15,58,125,0.08)",
            margin: "0 0 5px",
          }}
        />

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: 14,
            fontWeight: 700,
            color: "#5c6f8f",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <span>{(row.achieved ?? 0).toLocaleString("en-IN")}</span>
<span style={{ color: "#94a3b8" }}>
  / {(row.target ?? 0).toLocaleString("en-IN")} STB
</span>
        </div>
        <div
          style={{
            height: 6,
            borderRadius: 3,
            background: "#eef1f6",
            overflow: "hidden",
            marginTop: 6,
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${Math.min(pct, 100)}%`,
              background: barColor,
              transition: "width .6s ease",
            }}
          />
        </div>
      </div>
    </div>
  );
}

function ManagerIdCardSkeleton({ delay = 0 }) {
  const block = (style) => (
    <span className="so-skeleton-block" style={{ borderRadius: 6, ...style }} />
  );
  return (
    <div
      className="so-mgr-id-card"
      style={{
        background: "#fff",
        borderRadius: 14,
        overflow: "hidden",
        border: "1px solid rgba(15,58,125,0.08)",
        boxShadow: "0 6px 16px rgba(15,58,125,0.09)",
        display: "flex",
        flexDirection: "column",
        animation: "so-card-in .45s ease both",
        animationDelay: `${delay}ms`,
      }}
    >
      <div
        className="so-mgr-photo"
        style={{
          position: "relative",
          width: "100%",
          aspectRatio: "9/7",
          background: "#e7edf7",
          overflow: "hidden",
        }}
      >
        {block({ position: "absolute", inset: 0, borderRadius: 0 })}
        {block({
          position: "absolute",
          top: 6,
          right: 6,
          width: 34,
          height: 18,
          borderRadius: 999,
          background: "#d7e0f0",
        })}
      </div>
      <div
        style={{
          padding: "10px 10px 12px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 8,
        }}
      >
        {block({ width: "62%", height: 14 })}
        <div
          style={{
            height: 1,
            width: "100%",
            background: "rgba(15,58,125,0.08)",
            margin: "2px 0",
          }}
        />
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            gap: 8,
          }}
        >
          {block({ width: "35%", height: 12 })}
          {block({ width: "40%", height: 12 })}
        </div>
        {block({ width: "100%", height: 6, borderRadius: 3 })}
      </div>
    </div>
  );
}

function XlsxIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <path
        d="M6 2h9l5 5v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2Z"
        fill="#1a7f4b"
      />
      <path d="M15 2v5h5" fill="#0f5c36" />
      <text
        x="12"
        y="18"
        textAnchor="middle"
        style={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize: 7.5,
          fontWeight: 800,
          fill: "#fff",
        }}
      >
        XLS
      </text>
    </svg>
  );
}
function PdfIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <path
        d="M6 2h9l5 5v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2Z"
        fill="#d63a3a"
      />
      <path d="M15 2v5h5" fill="#9c2626" />
      <text
        x="12"
        y="18"
        textAnchor="middle"
        style={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize: 7.5,
          fontWeight: 800,
          fill: "#fff",
        }}
      >
        PDF
      </text>
    </svg>
  );
}

function AnimatedProgressBar({ pct, color, delay = 0 }) {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    setWidth(0);
    const t = setTimeout(() => setWidth(Math.min(pct, 100)), delay);
    return () => clearTimeout(t);
  }, [pct, delay]);
  return (
    <div className="so-race-track">
      <div
        className="so-race-fill"
        style={{
          width: `${width}%`,
          background: color,
          transition: "width 1s cubic-bezier(.22,1,.36,1)",
        }}
      >
        <span className="so-race-shimmer" />
      </div>
    </div>
  );
}

function AchievementBadge({ pct }) {
  const tier =
    pct >= 100 ? "gold" : pct >= 70 ? "amber" : pct > 0 ? "red" : "flat";
  const icon = pct >= 100 ? "🏆" : pct >= 70 ? "⚡" : pct > 0 ? "⚠" : "•";
  const styles = {
    gold: { bg: "linear-gradient(135deg,#34d399,#059669)", fg: "#fff" },
    amber: { bg: "linear-gradient(135deg,#fbbf24,#d97706)", fg: "#3a2600" },
    red: { bg: "linear-gradient(135deg,#f87171,#dc2626)", fg: "#fff" },
    flat: { bg: "#e5edf7", fg: "#6b7c96" },
  }[tier];
  return (
    <span
      className="so-ach-badge"
      style={{ background: styles.bg, color: styles.fg }}
    >
      <span style={{ marginRight: 4 }}>{icon}</span>
      {pct}%
    </span>
  );
}

function TarVsAchTable({ rows, month }) {
  const sorted = useMemo(
    () => [...rows].sort((a, b) => a.name.localeCompare(b.name)),
    [rows],
  );

  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, r) => ({
          stb_issued: acc.stb_issued + r.stb_issued,
          stb_tobe_issued: acc.stb_tobe_issued + r.stb_tobe_issued,
          target: acc.target + r.target,
          achieved: acc.achieved + r.achieved,
        }),
        { stb_issued: 0, stb_tobe_issued: 0, target: 0, achieved: 0 },
      ),
    [rows],
  );
  const totalPct = totals.target
    ? Math.round((totals.achieved / totals.target) * 1000) / 10
    : 0;

  if (!rows.length) {
    return <p className="so-empty">No records for {monthLabel(month)} yet.</p>;
  }

  return (
    <div className="so-tar-table-wrap" style={{ overflowX: "auto" }}>
      <table
        className="so-tar-table"
        style={{ fontSize: 14.5, minWidth: 560, width: "100%" }}
      >
        <thead>
          <tr>
            <th>Name</th>
            <th>STB Issued</th>
            <th>To be Issued</th>
            <th>Target</th>
            <th>Ach</th>
            <th>Ach%</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((r, i) => {
            const pct = r.target
              ? Math.round((r.achieved / r.target) * 1000) / 10
              : 0;
            return (
              <tr
                key={r.name}
                className={i % 2 === 0 ? "so-tar-row-a" : "so-tar-row-b"}
                style={{
                  animation: "so-row-in .4s ease both",
                  animationDelay: `${i * 40}ms`,
                }}
              >
                <td className="so-tar-name">{r.name}</td>
                <td>{(r.stb_issued ?? 0).toLocaleString("en-IN")}</td>
                <td>{(r.stb_tobe_issued ?? 0).toLocaleString("en-IN")}</td>
                <td>{(r.target ?? 0).toLocaleString("en-IN")}</td>
                <td>{(r.achieved ?? 0).toLocaleString("en-IN")}</td>
                <td style={{ color: achTierColor(pct), fontWeight: 800 }}>
                  {pct}%
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="so-tar-total-row">
            <td>Total</td>
            <td>{(totals.stb_issued ?? 0).toLocaleString("en-IN")}</td>
            <td>{(totals.stb_tobe_issued ?? 0).toLocaleString("en-IN")}</td>
            <td>{(totals.target ?? 0).toLocaleString("en-IN")}</td>
            <td>{(totals.achieved ?? 0).toLocaleString("en-IN")}</td>
            <td>{totalPct}%</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function Table1BarChart({ rows }) {
  const isMobile = useMediaQuery("(max-width: 767px)");
  const isTablet = useMediaQuery("(max-width: 1024px)");
  const data = useMemo(
    () =>
      [...rows]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((r) => ({
          name: r.name.length > 10 ? `${r.name.slice(0, 9)}…` : r.name,
          fullName: r.name,
          target: r.target,
          achieved: r.achieved,
        })),
    [rows],
  );

  if (!data.length) return null;

  const chartHeight = isMobile ? 260 : isTablet ? 270 : 280;

  return (
    <ResponsiveContainer width="100%" height={chartHeight} minWidth={0}>
      <BarChart
        data={data}
        margin={
          isMobile
            ? { top: 8, right: 8, left: -14, bottom: 40 }
            : { top: 8, right: 16, left: 0, bottom: 4 }
        }
      >
        <CartesianGrid vertical={false} stroke="rgba(15,58,125,0.08)" />
        <XAxis
          dataKey="name"
          tick={{
            fontSize: isMobile ? 10.5 : 12.5,
            fontWeight: 700,
            fill: "#5c6f8f",
          }}
          tickLine={false}
          axisLine={{ stroke: "rgba(15,58,125,0.15)" }}
          interval={0}
          angle={isMobile ? -40 : 0}
          textAnchor={isMobile ? "end" : "middle"}
          height={isMobile ? 52 : 30}
        />
        <YAxis
          tick={{
            fontSize: isMobile ? 10.5 : 12.5,
            fontWeight: 700,
            fill: "#5c6f8f",
          }}
          tickLine={false}
          axisLine={false}
          tickFormatter={formatY}
          width={isMobile ? 32 : 40}
        />
        <Tooltip
          content={<BarTooltip />}
          cursor={{ fill: "rgba(15,58,125,0.05)" }}
        />
        <Legend
          wrapperStyle={{
            fontSize: isMobile ? 11.5 : 13.5,
            fontWeight: 700,
            color: "#5c6f8f",
          }}
        />
        <Bar
          dataKey="target"
          name="Target"
          fill="#2a5fd6"
          radius={[6, 6, 0, 0]}
          maxBarSize={isMobile ? 16 : 26}
        />
        <Bar
          dataKey="achieved"
          name="Achieved"
          fill="#1fd393"
          radius={[6, 6, 0, 0]}
          maxBarSize={isMobile ? 16 : 26}
        />
      </BarChart>
    </ResponsiveContainer>
  );
}

function MonthlyDetailsTable({ tableRows, downloadingKey, onDownload }) {
  useEffect(() => {
    injectKeyframesOnce();
  }, []);

  const totalRows = tableRows.length;

  return (
    <div className="so-race-table-wrap" style={{ overflowX: "auto" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 12,
          marginBottom: 16,
          paddingBottom: 12,
          borderBottom: "1px solid rgba(15,58,125,0.1)",
        }}
      >
        <div style={{ color: "#5c6f8f", fontSize: 14.5, fontWeight: 600 }}>
          Showing all <strong style={{ color: "#0f3a7d" }}>{totalRows}</strong>{" "}
          months
        </div>
      </div>

      {tableRows.length === 0 ? (
        <p className="so-empty">No records yet.</p>
      ) : (
        <div className="so-race-table" style={{ minWidth: 760 }}>
          <div className="so-race-thead" style={{ fontSize: 12.5 }}>
            <div className="so-race-th" style={{ flex: 1.1 }}>
              Month
            </div>
            <div className="so-race-th" style={{ flex: 1 }}>
              Target
            </div>
            <div className="so-race-th" style={{ flex: 1.3 }}>
              To Be Issued
            </div>
            <div className="so-race-th" style={{ flex: 1 }}>
              Issued
            </div>
            <div className="so-race-th" style={{ flex: 1 }}>
              Achieved
            </div>
            <div className="so-race-th" style={{ flex: 2.2 }}>
              Achievement
            </div>
            <div
              className="so-race-th"
              style={{ flex: 1.4, textAlign: "right" }}
            >
              Export
            </div>
          </div>

          {tableRows.map((r, i) => {
            const pct = r.target
              ? Math.round((r.achieved / r.target) * 100)
              : 0;
            const barColor =
              pct >= 100
                ? "linear-gradient(90deg,#34d399,#059669)"
                : pct >= 70
                  ? "linear-gradient(90deg,#fbbf24,#f59e0b)"
                  : "linear-gradient(90deg,#f87171,#dc2626)";
            const xlsxKey = `${r.month}-xlsx`;
            const pdfKey = `${r.month}-pdf`;
            return (
              <div
                className="so-race-row"
                key={r.id ?? r.month}
                style={{
                  animation: `so-row-in .5s ease both`,
                  animationDelay: `${i * 55}ms`,
                  fontSize: 14.5,
                }}
              >
                <div className="so-race-td so-race-month" style={{ flex: 1.1 }}>
                  <span className="so-race-month-dot" />
                  {monthLabel(r.month)}
                </div>
                <div className="so-race-td" style={{ flex: 1 }}>
                  {r.target.toLocaleString("en-IN")}
                </div>
                <div
                  className="so-race-td"
                  style={{ flex: 1.3, color: "#7b8bb0" }}
                >
                  {r.stb_tobe_issued.toLocaleString("en-IN")}
                </div>
                <div className="so-race-td" style={{ flex: 1 }}>
                  {r.stb_issued.toLocaleString("en-IN")}
                </div>
                <div
                  className="so-race-td so-race-achieved"
                  style={{ flex: 1 }}
                >
                  {r.achieved.toLocaleString("en-IN")}
                </div>
                <div className="so-race-td" style={{ flex: 2.2 }}>
                  <div className="so-race-ach-cell">
                    <AnimatedProgressBar
                      pct={pct}
                      color={barColor}
                      delay={i * 55 + 150}
                    />
                    <AchievementBadge pct={pct} />
                  </div>
                </div>
                <div
                  className="so-race-td"
                  style={{
                    flex: 1.4,
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 6,
                  }}
                >
                  <button
                    type="button"
                    className="so-export-btn so-export-xlsx"
                    disabled={downloadingKey === xlsxKey}
                    onClick={() => onDownload(r.month, "xlsx")}
                    title="Export as Excel"
                  >
                    {downloadingKey === xlsxKey ? (
                      <span className="so-export-spinner" />
                    ) : (
                      <XlsxIcon />
                    )}
                  </button>
                  <button
                    type="button"
                    className="so-export-btn so-export-pdf"
                    disabled={downloadingKey === pdfKey}
                    onClick={() => onDownload(r.month, "pdf")}
                    title="Export as PDF"
                  >
                    {downloadingKey === pdfKey ? (
                      <span className="so-export-spinner" />
                    ) : (
                      <PdfIcon />
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ManagerFilterSelect({
  isRestrictedSalesManager,
  visibleManagers,
  value,
  onChange,
  managersLoading,
  managersError,
  onRetryManagers,
}) {
  if (managersError) {
    return (
      <div className="so-error-row" style={{ margin: 0 }}>
        <span>⚠️ {managersError}</span>
        <button className="so-retry-btn" onClick={onRetryManagers}>
          Retry
        </button>
      </div>
    );
  }
  if (managersLoading) {
    return (
      <span className="so-empty" style={{ margin: 0 }}>
        Loading sales managers...
      </span>
    );
  }

  return (
    <select
      className="so-topbar-select"
      value={value}
      disabled={isRestrictedSalesManager}
      onChange={(e) => onChange(e.target.value)}
      style={{
        height: 42,
        padding: "0 34px 0 16px",
        borderRadius: 10,
        border: "1px solid #d7e0f0",
        background: isRestrictedSalesManager ? "#f3f6fb" : "#fff",
        color: "#1e3a5f",
        fontWeight: 700,
        fontSize: 14,
        minWidth: 240,
        maxWidth: "100%",
        cursor: isRestrictedSalesManager ? "default" : "pointer",
      }}
    >
      {!isRestrictedSalesManager && (
        <option value="all">📊 All Managers</option>
      )}
      {visibleManagers.map((m) => (
        <option key={m.id} value={String(m.id)}>
          👤 {m.name}
        </option>
      ))}
    </select>
  );
}

function TargetRowsSkeleton({ count = 5 }) {
  return (
    <div className="so-modal-rows">
      {Array.from({ length: count }).map((_, i) => (
        <div
          className="so-target-row"
          key={i}
          style={{
            animation: "so-row-in .4s ease both",
            animationDelay: `${i * 60}ms`,
          }}
        >
          <span
            className="so-skeleton-block"
            style={{ width: 34, height: 34, borderRadius: "50%" }}
          />
          <span
            className="so-skeleton-block"
            style={{ width: 120, height: 14, borderRadius: 6 }}
          />
          <span
            className="so-skeleton-block"
            style={{ flex: 1, height: 38, borderRadius: 10, minWidth: 100 }}
          />
          <span
            className="so-skeleton-block"
            style={{ width: 70, height: 34, borderRadius: 10 }}
          />
        </div>
      ))}
    </div>
  );
}

function SetTargetModal({ open, onClose, managers, onSaved }) {
  const month = currentMonth();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [applyAllValue, setApplyAllValue] = useState("");
  const [applyAllError, setApplyAllError] = useState("");
  const [applyAllSaving, setApplyAllSaving] = useState(false);
  const [applyAllProgress, setApplyAllProgress] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setApplyAllValue("");
    setApplyAllError("");
    setApplyAllProgress(0);
    setConfirmOpen(false);

    (async () => {
      setLoading(true);
      setLoadError("");
      try {
        const res = await getSalesOverview(undefined, month);
        const dataRows = res.data ?? [];
        const byManagerId = new Map();
        const byName = new Map();
        dataRows.forEach((r) => {
          const mid = r.sales_manager_id ?? r.manager_id ?? null;
          const t = Number(r.target) || 0;
          if (mid != null) byManagerId.set(Number(mid), t);
          if (r.name) byName.set(r.name, t);
        });
        if (!cancelled) {
          setRows(
            managers.map((m) => {
              const existing =
                byManagerId.get(Number(m.id)) ?? byName.get(m.name) ?? 0;
              const has = existing > 0;
              return {
                id: m.id,
                name: m.name,
                existingTarget: has ? existing : null,
                value: has ? String(existing) : "",
                editing: !has,
                saving: false,
                error: "",
              };
            }),
          );
        }
      } catch (e) {
        if (!cancelled)
          setLoadError(e.message || "Failed to load current targets");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, managers, month]);

  const updateRow = (id, patch) =>
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const saveRow = async (row) => {
    const val = Number(row.value);
    if (!row.value || val <= 0) {
      updateRow(row.id, { error: "Enter a valid target" });
      return;
    }
    updateRow(row.id, { saving: true, error: "" });
    try {
      await setSalesTarget({ sales_manager_id: row.id, month, target: val });
      updateRow(row.id, { saving: false, editing: false, existingTarget: val });
      onSaved?.({ managerName: row.name, month, target: val });
    } catch (e) {
      updateRow(row.id, {
        saving: false,
        error: e.message || "Failed to save",
      });
    }
  };

  const handleApplyAllClick = () => {
    if (!applyAllValue || Number(applyAllValue) <= 0) {
      setApplyAllError("Enter a valid target value first");
      return;
    }
    setApplyAllError("");
    setConfirmOpen(true);
  };

  const handleApplyAllConfirmed = async () => {
    const val = Number(applyAllValue);
    setApplyAllSaving(true);
    setApplyAllProgress(0);
    setApplyAllError("");
    try {
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        // eslint-disable-next-line no-await-in-loop
        await setSalesTarget({ sales_manager_id: row.id, month, target: val });
        setApplyAllProgress(i + 1);
      }
      setRows((prev) =>
        prev.map((r) => ({
          ...r,
          existingTarget: val,
          value: String(val),
          editing: false,
          error: "",
        })),
      );
      onSaved?.({
        managerName: `All ${rows.length} Managers`,
        month,
        target: val,
      });
      setConfirmOpen(false);
      setApplyAllValue("");
    } catch (e) {
      setApplyAllError(e.message || "Failed to apply target to all managers");
    } finally {
      setApplyAllSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="so-modal-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !applyAllSaving) onClose();
      }}
    >
      <div className="so-modal-card">
        <div className="so-modal-header">
          <div>
            <div className="so-modal-title">🎯 Set Sales Target</div>
            <div className="so-modal-subtitle">
              Current month only · {monthLabelLong(month)}
            </div>
          </div>
          <button
            type="button"
            className="so-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="so-modal-apply-all">
          <div className="so-field" style={{ flex: 1, minWidth: 180 }}>
            <label className="so-field-label">
              Apply same target to all managers
            </label>
            <input
              className="so-field-input"
              type="number"
              min="1"
              placeholder="e.g. 600"
              value={applyAllValue}
              disabled={applyAllSaving}
              onChange={(e) => {
                setApplyAllValue(e.target.value);
                setApplyAllError("");
              }}
            />
          </div>
          <div className="so-field so-field-btn">
            <label className="so-field-label">&nbsp;</label>
            <button
              type="button"
              className="so-submit-btn so-apply-all-btn"
              disabled={applyAllSaving || loading || rows.length === 0}
              onClick={handleApplyAllClick}
            >
              {applyAllSaving
                ? `Applying ${applyAllProgress}/${rows.length}...`
                : `Apply to All (${rows.length})`}
            </button>
          </div>
        </div>
        {applyAllSaving && (
          <div style={{ marginTop: 4, marginBottom: 4 }}>
            <AnimatedProgressBar
              pct={
                rows.length
                  ? Math.round((applyAllProgress / rows.length) * 100)
                  : 0
              }
              color="linear-gradient(90deg,#0f3a7d,#00a3d9)"
            />
          </div>
        )}
        {applyAllError && (
          <span className="so-saved-msg so-saved-msg-error">
            ⚠️ {applyAllError}
          </span>
        )}

        <div className="so-modal-divider" />

        {loading ? (
          <TargetRowsSkeleton count={managers.length || 5} />
        ) : loadError ? (
          <div className="so-error-row">
            <span>⚠️ {loadError}</span>
          </div>
        ) : (
          <div className="so-modal-rows">
            {rows.map((row) => (
              <div className="so-target-row" key={row.id}>
                <span className="so-target-row-avatar">
                  {initials(row.name)}
                </span>
                <span className="so-target-row-name">{row.name}</span>

                <div className="so-target-row-input-wrap">
                  <input
                    className="so-field-input so-target-row-input"
                    type="number"
                    min="1"
                    placeholder="Target STB"
                    value={row.value}
                    disabled={!row.editing || row.saving}
                    onChange={(e) =>
                      updateRow(row.id, { value: e.target.value, error: "" })
                    }
                  />
                  {row.error && (
                    <span className="so-target-row-error">{row.error}</span>
                  )}
                </div>

                {row.editing ? (
                  <button
                    type="button"
                    className="so-submit-btn so-target-row-save"
                    disabled={row.saving}
                    onClick={() => saveRow(row)}
                  >
                    {row.saving ? (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <span className="so-export-spinner" />
                        Saving...
                      </span>
                    ) : (
                      "Save"
                    )}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="so-edit-icon-btn"
                    title="Edit target"
                    onClick={() => updateRow(row.id, { editing: true })}
                  >
                    ✎ Edit
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {confirmOpen && (
        <div
          className="so-confirm-overlay"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !applyAllSaving)
              setConfirmOpen(false);
          }}
        >
          <div className="so-confirm-card">
            <div className="so-confirm-title">
              Apply target to all managers?
            </div>
            <p className="so-confirm-body">
              This sets the target to{" "}
              <strong>{Number(applyAllValue).toLocaleString("en-IN")}</strong>{" "}
              STB for <strong>all {rows.length} managers</strong> for{" "}
              {monthLabelLong(month)}, overwriting any existing target already
              saved for this month.
            </p>
            <div className="so-confirm-actions">
              <button
                type="button"
                className="so-retry-btn"
                disabled={applyAllSaving}
                onClick={() => setConfirmOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="so-submit-btn"
                disabled={applyAllSaving}
                onClick={handleApplyAllConfirmed}
              >
                {applyAllSaving
                  ? `Applying ${applyAllProgress}/${rows.length}...`
                  : "Yes, Apply to All"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PageSkeleton() {
  useEffect(() => {
    injectKeyframesOnce();
  }, []);

  const block = (style) => (
    <span
      className="so-skeleton-block"
      style={{ borderRadius: 10, ...style }}
    />
  );

  return (
    <div className="so-container" style={{ padding: "10px 16px" }}>
      {/* top bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          padding: "8px 14px",
          borderRadius: 12,
          background: "#dbe3f0",
          marginBottom: 14,
          flexWrap: "wrap",
        }}
      >
        {block({ width: 34, height: 34, borderRadius: "50%" })}
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {block({ width: 140, height: 14 })}
          {block({ width: 190, height: 10 })}
        </div>
        <div style={{ flex: 1 }} />
        {block({ width: 150, height: 36, borderRadius: 9 })}
        {block({ width: 90, height: 36, borderRadius: 9 })}
        {block({ width: 110, height: 36, borderRadius: 9 })}
      </div>
      <div
        style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 20 }}
      >
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            style={{
              flex: 1,
              minWidth: 130,
              background: "#fff",
              borderRadius: 12,
              padding: "10px 14px",
              boxShadow: "0 2px 10px rgba(15,58,125,0.08)",
            }}
          >
            {block({ width: 90, height: 10, marginBottom: 10 })}
            {block({ width: 70, height: 22 })}
          </div>
        ))}
      </div>
      <div className="so-mgr-grid" style={{ marginBottom: 20 }}>
        {Array.from({ length: 8 }).map((_, i) => (
          <div
            key={i}
            style={{
              background: "#fff",
              borderRadius: 18,
              overflow: "hidden",
              border: "1px solid rgba(15,58,125,0.08)",
            }}
          >
            {block({ width: "100%", height: 150, borderRadius: 0 })}
            <div
              style={{
                padding: "38px 16px 20px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 8,
              }}
            >
              {block({ width: 120, height: 16 })}
              {block({ width: 80, height: 10 })}
              {block({ width: "100%", height: 8, marginTop: 10 })}
            </div>
          </div>
        ))}
      </div>

      <div className="so-card" style={{ marginBottom: 20 }}>
        {block({ width: 220, height: 16, marginBottom: 16 })}
        {block({ width: "100%", height: 220 })}
      </div>

      <div className="so-card">
        {block({ width: 260, height: 16, marginBottom: 16 })}
        {block({ width: "100%", height: 260, marginBottom: 16 })}
        {block({ width: "100%", height: 300 })}
      </div>
    </div>
  );
}

export default function SalesOverview() {
  const { isRestrictedSalesManager, restrictedManagerId, canSetTarget } =
    useAccessControl();

  const [managers, setManagers] = useState([]);
  const [managersLoading, setManagersLoading] = useState(true);
  const [managersError, setManagersError] = useState("");
  const [managersReady, setManagersReady] = useState(false);

  const [toastData, setToastData] = useState(null);
  const [targetModalOpen, setTargetModalOpen] = useState(false);

  const [monthFilter, setMonthFilter] = useState(currentMonth());
  const [monthRows, setMonthRows] = useState([]);
  const [monthLoading, setMonthLoading] = useState(false);
  const [monthError, setMonthError] = useState("");

  const [monthReady, setMonthReady] = useState(false);

  const [graphMode, setGraphMode] = useState("all");
  const [graphManagerId, setGraphManagerId] = useState(null);

  const [overviewData, setOverviewData] = useState({});
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewError, setOverviewError] = useState("");
  const [overviewReady, setOverviewReady] = useState(false);

  const [downloadingKey, setDownloadingKey] = useState(null);
  const [downloadError, setDownloadError] = useState("");

  const headerSlot = useHeaderSlot();
  const isMobile = useMediaQuery("(max-width: 767px)");

  const chartStageRef = useRef(null);
  const [plotWidth, setPlotWidth] = useState(600);

  useEffect(() => {
    injectKeyframesOnce();
  }, []);

  useEffect(() => {
    const el = chartStageRef.current;
    if (!el) return;
    const update = () => {
      const w = el.getBoundingClientRect().width;
      if (w > 0) setPlotWidth(w - 50);
    };
    update();
    const ro =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
    ro?.observe(el);
    window.addEventListener("resize", update);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [graphMode, graphManagerId]);

  const TargetRaceDot = useMemo(
    () => RaceDot("#6d93ff", plotWidth, "up"),
    [plotWidth],
  );
  const AchievedRaceDot = useMemo(
    () => RaceDot("#4be3ac", plotWidth, "down"),
    [plotWidth],
  );

  const fetchMonthWiseOverview = useCallback(
    async (month) => {
      if (!month) return;
      setMonthLoading(true);
      setMonthError("");
      try {
        if (isRestrictedSalesManager) {
          const res = await getSalesOverview(restrictedManagerId, month);
          const ownName =
            managers.find((m) => Number(m.id) === Number(restrictedManagerId))
              ?.name || getCurrentUser();
          const rows = (res.data ?? []).map((r) => ({
            ...normalizeRow(r),
            id: r.sales_manager_id ?? r.manager_id ?? restrictedManagerId,
            name: ownName,
          }));
          setMonthRows(rows);
        } else {
          const res = await getSalesOverview(undefined, month);
          const rows = (res.data ?? []).map((r) => ({
            ...normalizeRow(r),
            id: r.sales_manager_id ?? r.manager_id ?? r.id,
            name: r.name,
          }));
          setMonthRows(rows);
        }
      } catch (e) {
        setMonthError(e.message || "Failed to load monthly overview");
      } finally {
        setMonthLoading(false);
        setMonthReady(true);
      }
    },
    [isRestrictedSalesManager, restrictedManagerId, managers],
  );

  const fetchOverview = useCallback(
    async (managerId) => {
      if (!managerId) return;
      if (
        isRestrictedSalesManager &&
        Number(managerId) !== Number(restrictedManagerId)
      ) {
        return;
      }
      setOverviewLoading(true);
      setOverviewError("");
      try {
        const res = await getSalesOverview(managerId);
        const rows = (res.data ?? []).map(normalizeRow);
        setOverviewData((prev) => ({ ...prev, [managerId]: rows }));
      } catch (e) {
        setOverviewError(e.message || "Failed to load sales overview");
      } finally {
        setOverviewLoading(false);
        setOverviewReady(true);
      }
    },
    [isRestrictedSalesManager, restrictedManagerId],
  );

  const fetchAllOverview = useCallback(async () => {
    if (isRestrictedSalesManager) return;
    setOverviewLoading(true);
    setOverviewError("");
    try {
      const res = await getSalesOverviewAll();
      const rows = (res.data ?? []).map(normalizeRow);
      setOverviewData((prev) => ({ ...prev, __all__: rows }));
    } catch (e) {
      setOverviewError(
        e.message || "Failed to load all-managers sales overview",
      );
    } finally {
      setOverviewLoading(false);
      setOverviewReady(true);
    }
  }, [isRestrictedSalesManager]);

  const fetchManagers = async () => {
    setManagersLoading(true);
    setManagersError("");
    try {
      const res = await getSalesManagers();
      const list = res.data ?? res.items ?? [];
      setManagers(list);

      if (isRestrictedSalesManager) {
        setGraphMode("individual");
        setGraphManagerId(restrictedManagerId);
      } else if (list.length > 0) {
        setGraphManagerId((prev) => prev ?? list[0].id);
      }
    } catch (e) {
      setManagersError(e.message || "Failed to load sales managers");
    } finally {
      setManagersLoading(false);
      setManagersReady(true);
    }
  };

  useEffect(() => {
    fetchManagers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!managersLoading) {
      fetchMonthWiseOverview(monthFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [managersLoading]);

  useEffect(() => {
    if (graphMode !== "all") return;
    fetchAllOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graphMode]);

  useEffect(() => {
    if (graphMode !== "individual" || !graphManagerId) return;
    fetchOverview(graphManagerId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graphMode, graphManagerId]);

  const visibleManagers = isRestrictedSalesManager
    ? managers.filter((m) => Number(m.id) === Number(restrictedManagerId))
    : managers;

  const filledMonthRows = useMemo(
    () =>
      isRestrictedSalesManager
        ? monthRows
        : fillAllManagerRows(monthRows, managers, monthFilter),
    [isRestrictedSalesManager, monthRows, managers, monthFilter],
  );

  const rankedMonthRows = useMemo(() => {
    const pctOf = (r) => (r.target ? (r.achieved / r.target) * 100 : -1);
    return [...filledMonthRows].sort((a, b) => {
      const diff = pctOf(b) - pctOf(a);
      if (diff !== 0) return diff;
      return (a.name || "").localeCompare(b.name || "");
    });
  }, [filledMonthRows]);

  const monthTotals = useMemo(
    () =>
      monthRows.reduce(
        (acc, r) => ({
          target: acc.target + r.target,
          stb_issued: acc.stb_issued + r.stb_issued,
          stb_tobe_issued: acc.stb_tobe_issued + r.stb_tobe_issued,
          achieved: acc.achieved + r.achieved,
        }),
        { target: 0, stb_issued: 0, stb_tobe_issued: 0, achieved: 0 },
      ),
    [monthRows],
  );
  const monthAchievementRate = monthTotals.target
    ? Math.round((monthTotals.achieved / monthTotals.target) * 100)
    : 0;

  const activeKey = graphMode === "all" ? "__all__" : graphManagerId;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const rows = overviewData[activeKey] || [];

  const graphManagerName =
    graphMode === "all"
      ? "All Managers"
      : managers.find((m) => m.id === graphManagerId)?.name || "";

  const chartData = useMemo(() => {
    const latest12Months = rows
      .slice()
      .sort((a, b) => toMonthKey(a.month).localeCompare(toMonthKey(b.month)))
      .slice(-12);

    return latest12Months.map((r, idx) => ({
      idx,
      month: monthLabel(r.month),
      Target: r.target,
      Achieved: r.achieved,
    }));
  }, [rows]);

  const monthByIdx = useMemo(() => chartData.map((d) => d.month), [chartData]);

  const yMax = useMemo(() => {
    const max = chartData.reduce(
      (m, d) => Math.max(m, d.Target, d.Achieved),
      0,
    );
    return Math.ceil(max * 1.15) || 10;
  }, [chartData]);

  const race = useLineChartRace(chartData, {
    msPerMonth: 2600,
    autoPlayKey: activeKey,
  });

  const visibleChartData = useMemo(() => {
    const n = chartData.length;
    if (!n) return [];

    const floorIdx = Math.min(Math.floor(race.progress), n - 1);
    const frac = race.progress - floorIdx;

    const points = chartData
      .slice(0, floorIdx + 1)
      .map((d) => ({ ...d, isLeader: false }));

    if (frac > 0.001 && floorIdx + 1 < n) {
      const a = chartData[floorIdx];
      const b = chartData[floorIdx + 1];

      points.push({
        idx: floorIdx + frac,
        month: b.month,
        Target: a.Target + (b.Target - a.Target) * frac,
        Achieved: a.Achieved + (b.Achieved - a.Achieved) * frac,
        isLeader: true,
      });
    } else if (points.length) {
      points[points.length - 1].isLeader = true;
    }

    return points;
  }, [chartData, race.progress]);

  const tableRows = useMemo(() => {
    return [...rows]
      .sort((a, b) => toMonthKey(b.month).localeCompare(toMonthKey(a.month)))
      .slice(0, 12);
  }, [rows]);

  const handleTargetSaved = useCallback(
    ({ managerName, month, target }) => {
      setToastData({ managerName, month, target });
      if (monthFilter === month) fetchMonthWiseOverview(monthFilter);
      if (graphMode === "all") fetchAllOverview();
      else if (graphManagerId) fetchOverview(graphManagerId);
    },
    [
      monthFilter,
      graphMode,
      graphManagerId,
      fetchMonthWiseOverview,
      fetchAllOverview,
      fetchOverview,
    ],
  );

  const handleDownload = async (month, format) => {
    if (!month) return;

    if (graphMode === "all") {
      const key = `${month}-${format}`;
      setDownloadingKey(key);
      setDownloadError("");
      try {
        if (format === "pdf") {
          const [res, operatorDetailRes] = await Promise.all([
            getSalesOverview(undefined, month),
            getSalesOverviewAllOperatorDetail(month),
          ]);
          const managerRows = (res.data ?? []).map((r) => ({
            name: r.name,
            target: Number(r.target) || 0,
            stb_issued: Number(r.stb_issued) || 0,
            stb_tobe_issued: Number(r.stb_tobe_issued) || 0,
            achieved: Number(r.achieved) || 0,
          }));
          exportSalesOverviewAllManagersPdf({
            managerRows,
            month,
            operatorDetails: operatorDetailRes.data ?? [],
          });
        } else {
          await downloadSalesOverviewExportAll(month);
        }
      } catch (err) {
        setDownloadError(err.message || "Failed to export");
      } finally {
        setDownloadingKey(null);
      }
      return;
    }

    if (!graphManagerId) return;
    if (
      isRestrictedSalesManager &&
      Number(graphManagerId) !== Number(restrictedManagerId)
    ) {
      return;
    }
    const key = `${month}-${format}`;
    setDownloadingKey(key);
    setDownloadError("");
    try {
      if (format === "pdf") {
        const detailRes = await getSalesOverviewOperatorDetail(
          graphManagerId,
          month,
        );
        exportSalesOverviewPdf({
          rows,
          managerName: graphManagerName,
          singleMonth: month,
          pairings: detailRes.data ?? [],
        });
      } else {
        await downloadSalesOverviewExport(graphManagerId, month);
      }
    } catch (err) {
      setDownloadError(err.message || `Failed to export`);
    } finally {
      setDownloadingKey(null);
    }
  };

  const handleFilterSelectChange = (val) => {
    if (val === "all") {
      setGraphMode("all");
    } else {
      setGraphMode("individual");
      setGraphManagerId(Number(val));
    }
  };
  const filterSelectValue =
    graphMode === "all" ? "all" : String(graphManagerId ?? "");

  const pageReady = managersReady && monthReady && overviewReady;
  if (!pageReady) {
    return <PageSkeleton />;
  }

  const topBarNode = (
    <CompactTopBar
      managerCount={visibleManagers.length}
      monthFilter={monthFilter}
      setMonthFilter={setMonthFilter}
      onSearch={() => fetchMonthWiseOverview(monthFilter)}
      monthLoading={monthLoading}
      canSetTarget={canSetTarget}
      onSetTarget={() => setTargetModalOpen(true)}
    />
  );

  return (
    <div
      className="so-container so-fullscreen"
      style={{
        padding: "10px 16px",
        height: "auto",
        maxHeight: "none",
        minHeight: "auto",
        overflow: "visible",
      }}
    >
      {headerSlot ? createPortal(topBarNode, headerSlot) : topBarNode}

      {monthError && (
        <div className="so-error-row" style={{ flexShrink: 0 }}>
          <span>⚠️ {monthError}</span>
          <button
            className="so-retry-btn"
            onClick={() => fetchMonthWiseOverview(monthFilter)}
          >
            Retry
          </button>
        </div>
      )}

      <div
        className="so-summary-row-responsive"
        style={{ display: "flex", gap: 14, flexWrap: "wrap", flexShrink: 0 }}
      >
        <MiniSummaryCard
          label="Total Target"
          emoji="🎯"
          value={monthTotals.target}
          accent="#0f3a7d"
          // foot={monthLabel(monthFilter)}
        />
        <MiniSummaryCard
          label="STB Issued"
          emoji="📦"
          value={monthTotals.stb_issued}
          accent="#00a3d9"
          // foot={monthLabel(monthFilter)}
        />
        <MiniSummaryCard
          label="To Be Issued"
          emoji="🕓"
          value={monthTotals.stb_tobe_issued}
          accent="#f59e0b"
          // foot="pending allotment"
        />
        <MiniSummaryCard
          label="Achieved"
          emoji="🏆"
          value={monthTotals.achieved}
          accent={monthAchievementRate >= 70 ? "#10b981" : "#ef4444"}
          // foot={`${monthAchievementRate}% of target`}
        />
      </div>

      <div
        className="so-scroll-panel"
        style={{ overflow: "visible", height: "auto", maxHeight: "none" }}
      >
        <div
          className="so-mgr-grid"
          style={{
            marginBottom: 20,
            ...(isRestrictedSalesManager
              ? {}
              : { height: "610px", width: "1220px" }),
          }}
        >
          {monthLoading
            ? Array.from({
                length: isRestrictedSalesManager
                  ? 1
                  : visibleManagers.length || 8,
              }).map((_, i) => <ManagerIdCardSkeleton key={i} delay={i * 60} />)
            : rankedMonthRows.map((r) => (
                <ManagerIdCard key={r.id ?? r.name} row={r} />
              ))}
        </div>

        <div className="so-card" style={{ marginBottom: 20 }}>
          <div
            className="so-card-top-bar"
            style={{ background: "linear-gradient(90deg,#10b981,#0f3a7d)" }}
          />
          <div className="so-card-title" style={{ fontSize: 17 }}>
            TAR vs ACH — {monthLabel(monthFilter)}
          </div>
          <div className="so-section-loading-wrap" style={{ minHeight: 360 }}>
            {monthLoading && (
              <div className="so-section-overlay">
                <span className="so-section-spinner" />
                <span className="so-section-overlay-text">
                  Loading {monthLabel(monthFilter)} data...
                </span>
              </div>
            )}
            <div className={monthLoading ? "so-section-dimmed" : undefined}>
              <TarVsAchTable rows={filledMonthRows} month={monthFilter} />
              {!isRestrictedSalesManager && (
                <>
                  <div
                    className="so-modal-divider"
                    style={{ margin: "18px 0" }}
                  />
                  <Table1BarChart rows={filledMonthRows} />
                </>
              )}
            </div>
          </div>
        </div>

        <div
          className="so-card"
          style={{ overflow: "hidden", marginBottom: 0 }}
        >
          <div
            className="so-card-top-bar"
            style={{ background: "linear-gradient(90deg,#00a3d9,#0f3a7d)" }}
          />
          <div
            className="so-card-title"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 10,
              fontSize: 17,
            }}
          >
            <span>Manager History — {graphManagerName}</span>
            <ManagerFilterSelect
              isRestrictedSalesManager={isRestrictedSalesManager}
              visibleManagers={visibleManagers}
              value={filterSelectValue}
              onChange={handleFilterSelectChange}
              managersLoading={managersLoading}
              managersError={managersError}
              onRetryManagers={fetchManagers}
            />
          </div>

          {overviewError ? (
            <div className="so-error-row">
              <span>⚠️ {overviewError}</span>
              <button
                className="so-retry-btn"
                onClick={() =>
                  graphMode === "all"
                    ? fetchAllOverview()
                    : fetchOverview(graphManagerId)
                }
              >
                Retry
              </button>
            </div>
          ) : (
            <div className="so-section-loading-wrap" style={{ minHeight: 420 }}>
              {overviewLoading && (
                <div className="so-section-overlay">
                  <span className="so-section-spinner" />
                  <span className="so-section-overlay-text">
                    Loading sales overview for {graphManagerName}...
                  </span>
                </div>
              )}

              <div
                className={overviewLoading ? "so-section-dimmed" : undefined}
              >
                {downloadError && (
                  <span className="so-saved-msg so-saved-msg-error">
                    ⚠️ {downloadError}
                  </span>
                )}
                <MonthlyDetailsTable
                  tableRows={tableRows}
                  downloadingKey={downloadingKey}
                  onDownload={handleDownload}
                />

                <div
                  className="so-modal-divider"
                  style={{ margin: "18px 0" }}
                />

                {chartData.length === 0 ? (
                  <p className="so-empty">
                    No data available for {graphManagerName} yet.
                  </p>
                ) : (
                  <>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: 10,
                        marginBottom: 10,
                      }}
                    >
                      <div className="so-legend" style={{ margin: 0 }}>
                        <span className="so-legend-pill">
                          <span
                            className="so-legend-swatch"
                            style={{
                              background:
                                "linear-gradient(135deg,#6d93ff,#2a5fd6)",
                            }}
                          />
                          Target
                        </span>
                        <span className="so-legend-pill">
                          <span
                            className="so-legend-swatch"
                            style={{
                              background:
                                "linear-gradient(135deg,#6ff5d4,#1fd393)",
                            }}
                          />
                          Achieved
                        </span>
                      </div>
                    </div>

                    <div
                      ref={chartStageRef}
                      style={{
                        borderRadius: 16,
                        padding: "18px 8px 6px",
                        background:
                          "radial-gradient(120% 140% at 50% 0%, #16264a 0%, #0a1730 55%, #060d1e 100%)",
                        boxShadow:
                          "inset 0 0 0 1px rgba(255,255,255,0.06), 0 12px 30px rgba(8,20,50,0.35)",
                        position: "relative",
                        overflow: "visible",
                      }}
                    >
                      <div
                        style={{
                          position: "absolute",
                          top: 14,
                          left: 18,
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          zIndex: 2,
                          padding: "4px 12px",
                          borderRadius: 999,
                          background: "rgba(255,255,255,0.08)",
                          backdropFilter: "blur(6px)",
                        }}
                      >
                        <span
                          style={{
                            width: 7,
                            height: 7,
                            borderRadius: "50%",
                            background: race.isPlaying ? "#ff5b6a" : "#7b8bb0",
                            animation: race.isPlaying
                              ? "so-rec-pulse 1.4s infinite"
                              : "none",
                          }}
                        />
                        <span
                          style={{
                            fontSize: 12.5,
                            fontWeight: 800,
                            letterSpacing: 0.4,
                            color: "#e7edff",
                            textTransform: "uppercase",
                          }}
                        >
                          {race.isPlaying ? "Playing" : "Paused"} ·{" "}
                          {monthByIdx[
                            Math.min(
                              Math.round(race.progress),
                              monthByIdx.length - 1,
                            )
                          ] || ""}
                        </span>
                      </div>

                      <ResponsiveContainer
                        width="100%"
                        height={isMobile ? 300 : 360}
                        minWidth={0}
                      >
                        <LineChart
                          data={visibleChartData}
                          margin={
                            isMobile
                              ? { top: 40, right: 16, left: -4, bottom: 6 }
                              : { top: 46, right: 40, left: 6, bottom: 6 }
                          }
                        >
                          <ChartDefs />
                          <CartesianGrid
                            vertical={false}
                            stroke="rgba(255,255,255,0.06)"
                            strokeDasharray="5 4"
                          />
                          <XAxis
                            dataKey="idx"
                            type="number"
                            domain={[0, Math.max(chartData.length - 1, 0)]}
                            ticks={chartData.map((d) => d.idx)}
                            tick={
                              <CustomXTick
                                monthByIdx={monthByIdx}
                                compact={isMobile}
                              />
                            }
                            tickLine={false}
                            axisLine={{
                              stroke: "rgba(255,255,255,0.18)",
                              strokeWidth: 1.5,
                            }}
                          />
                          <YAxis
                            tick={<CustomYTick compact={isMobile} />}
                            tickLine={false}
                            axisLine={false}
                            width={isMobile ? 34 : 46}
                            domain={[0, yMax]}
                          />
                          <Tooltip
                            content={<CustomTooltip monthByIdx={monthByIdx} />}
                            cursor={{
                              stroke: "rgba(255,255,255,0.18)",
                              strokeWidth: 1.5,
                            }}
                          />
                          <Line
                            type="monotone"
                            dataKey="Target"
                            stroke="url(#gradTargetLine)"
                            strokeWidth={3.5}
                            dot={(p) => (
                              <TargetRaceDot
                                key={`t-${p.index}`}
                                {...p}
                                dataLength={visibleChartData.length}
                              />
                            )}
                            isAnimationActive={false}
                          />
                          <Line
                            type="monotone"
                            dataKey="Achieved"
                            stroke="url(#gradAchievedLine)"
                            strokeWidth={3.5}
                            dot={(p) => (
                              <AchievedRaceDot
                                key={`a-${p.index}`}
                                {...p}
                                dataLength={visibleChartData.length}
                              />
                            )}
                            isAnimationActive={false}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>

                    <div
                      style={{
                        marginTop: 14,
                        display: "flex",
                        alignItems: "center",
                        gap: 14,
                        flexWrap: "wrap",
                      }}
                    >
                      <button
                        type="button"
                        className="so-submit-btn"
                        style={{ minWidth: 96 }}
                        onClick={race.isPlaying ? race.pause : race.play}
                      >
                        {race.isPlaying ? "⏸ Pause" : "▶ Play"}
                      </button>
                      <button
                        type="button"
                        className="so-retry-btn"
                        onClick={race.replay}
                      >
                        ⟲ Replay
                      </button>

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 13,
                            fontWeight: 700,
                            color: "#5c6f8f",
                          }}
                        >
                          Speed
                        </span>
                        {SPEED_OPTIONS.map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => race.setSpeed(opt.value)}
                            className="so-retry-btn"
                            style={{
                              padding: "4px 10px",
                              background:
                                race.speed === opt.value
                                  ? "#0f3a7d"
                                  : undefined,
                              color:
                                race.speed === opt.value ? "#fff" : undefined,
                              borderColor:
                                race.speed === opt.value
                                  ? "#0f3a7d"
                                  : undefined,
                            }}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>

                      <div
                        style={{
                          flex: 1,
                          minWidth: 160,
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                        }}
                      >
                        <div className="so-progress-track" style={{ flex: 1 }}>
                          <div
                            style={{
                              height: "100%",
                              width: `${
                                chartData.length > 1
                                  ? Math.round(
                                      (race.progress / (chartData.length - 1)) *
                                        100,
                                    )
                                  : 0
                              }%`,
                              background:
                                "linear-gradient(90deg,#4d79ff,#0f9d63)",
                              transition: "width 0.12s linear",
                            }}
                          />
                        </div>
                        <span
                          className="so-badge"
                          style={{
                            background: "#eef3fb",
                            color: "#1e3a5f",
                            minWidth: 44,
                            textAlign: "center",
                            fontSize: 13,
                          }}
                        >
                          {chartData.length > 1
                            ? Math.round(
                                (race.progress / (chartData.length - 1)) * 100,
                              )
                            : 0}
                          %
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <SaveTargetToast
        show={!!toastData}
        onClose={() => setToastData(null)}
        managerName={toastData?.managerName}
        month={toastData?.month}
        target={toastData?.target}
      />

      <SetTargetModal
        open={targetModalOpen}
        onClose={() => setTargetModalOpen(false)}
        managers={managers}
        onSaved={handleTargetSaved}
      />
    </div>
  );
}
